<script setup lang="ts">
import { ref, watchEffect, computed } from 'vue';
import {
    getRoleMetadata,
    RoleType,
    type AnyRole,
    type RoleMetadata,
} from '@precon-tools-suite-by-shared/js-precon-rbac';
import { useColorResolver } from '@precon-tools-suite-by-shared/shared-colors-ui';

const props = defineProps<{
    role: AnyRole | null | undefined;
}>();

const metadata = ref<RoleMetadata>({
    roleType: RoleType.Client,
    isSuperUser: false,
    isImplicit: true,
    title: props.role ?? '??',
    description: null,
    badge: null,
});

watchEffect(async () => {
    if (!props.role) return;
    metadata.value = await getRoleMetadata(props.role);
});

const showBadge = computed(
    () => !!props.role && !metadata.value.isImplicit && !!metadata.value.badge
);

const colorResolver = useColorResolver();

const style = computed(() =>
    metadata.value?.roleType === RoleType.Team
        ? {
              'background-color': colorResolver(
                  metadata.value?.badge?.color,
                  '100'
              ).rgbString(),
              color: colorResolver(
                  metadata.value?.badge?.color,
                  '700'
              ).rgbString(),
          }
        : {
              color: colorResolver(
                  metadata.value?.badge?.color,
                  '300'
              ).rgbString(),
              'background-color': colorResolver(
                  metadata.value?.badge?.color,
                  '50'
              ).rgbString(),
          }
);

const label = computed(() => metadata.value?.badge?.label ?? props.role);

const tooltip = computed(() => metadata.value.title);
</script>

<template>
    <span
        v-if="showBadge"
        v-tooltip.right="{ value: tooltip, fitContent: true }"
        :style="style"
        >{{ label }}</span
    >
</template>

<style scoped lang="scss">
span {
    border-radius: var(--border-radius);
    padding: 0.25em 0.5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: 0.3px;
    cursor: default;
}
</style>
